import React, { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBi } from '../BiContext';
import { basculerGroupe } from './regroupements';
import './FiltresPanel.css';

const PERIODES = [
  { mois: 3, label: '3 derniers mois' },
  { mois: 6, label: '6 derniers mois' },
  { mois: 12, label: '12 derniers mois' },
  { mois: 24, label: '24 derniers mois' },
];

// Panneau de filtres à droite, repliable — le réflexe Power BI, conservé.
// Sections empilées (Période, Sociétés — d'autres dimensions s'ajouteront par
// rapport). Le nombre de filtres actifs reste visible quand il est replié.
// Garde-fou de développement : si le panneau finit hors de la fenêtre, on le
// dit tout de suite dans la console plutôt que d'attendre qu'un utilisateur
// signale « je dois défiler horizontalement pour trouver les filtres ».
// Le cas se produit quand un conteneur de page refuse de rétrécir (élément
// flex à `min-width: auto`) ; la règle `.bi-layout > :not(nav):not(aside)` de
// Shell.css l'empêche, ce contrôle est là pour le jour où elle sauterait.
function useAlerteHorsEcran(ref) {
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || !ref.current) return undefined;
    const verifier = () => {
      const el = ref.current;
      if (!el) return;
      const { left, width } = el.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      if (left >= vw - 2 || left + width > vw + 2) {
        // eslint-disable-next-line no-console
        console.warn(
          '[FiltresPanel] le panneau sort de la fenêtre (left=%d, largeur fenêtre=%d). ' +
          'La page voisine refuse probablement de rétrécir : vérifier que son conteneur ' +
          'est bien un enfant direct de .bi-layout et hérite de min-width: 0.',
          Math.round(left), vw,
        );
      }
    };
    verifier();
    window.addEventListener('resize', verifier);
    return () => window.removeEventListener('resize', verifier);
  }, [ref]);
}

function FiltresPanel({
  ouvert, onToggle,
  mois, onMois,
  // Période personnalisée (optionnelle) : liste [{code,label}] + valeur + setter.
  // Fournie à la place de la période glissante par mois (VN/VO : cumul année).
  periodes, periode, onPeriode,
  // Sélecteur de mois précis (optionnel) : moisDispo [{code:'YYYY-MM',label}],
  // moisActifs (array), onToggleMois. Un ou plusieurs mois cumulables.
  moisDispo, moisActifs, onToggleMois,
  societesDispo, societesActives, onToggleSociete, onToutesSocietes, onAucuneSociete,
  categoriesDispo, categoriesActives, onToggleCategorie, titreCategories,
  // Sections de REGROUPEMENT, au-dessus des sociétés : [{ titre, valeurs:
  // [{ code, nom, membres: [codeSociete] }] }]. Une section ne porte pas son
  // propre état : une valeur est cochée quand TOUS ses membres le sont, et la
  // cocher revient à cocher ses membres. Un seul état de filtrage (§4 bis).
  groupes, onToggleGroupe,
  // Choix EXCLUSIF (radios) : un périmètre à trois états dont l'un est « tout ».
  // Le canal du PR (Comptoir / Atelier / les deux) en est le premier usage : des
  // cases à cocher y auraient permis de tout décocher, c'est-à-dire de demander
  // un rapport sur rien. { titre, valeurs: [{ code, nom }], valeur, onChoisir } —
  // le code vide est le choix « tout ».
  choix,
  // Mois plus anciens, relégués dans une liste déroulante.
  moisAutres,
  // Place la section « catégories » APRÈS les sociétés (VN au jour le jour :
  // les marques se lisent après le périmètre, pas avant).
  categoriesApres,
  // Sans le bloc Sociétés (P&L et SIG, 2026-10-29) : les regroupements suffisent.
  sansSocietes,
  couleurDe, nbActifs, onReset,
}) {
  const ref = useRef(null);
  useAlerteHorsEcran(ref);
  // Concessions groupées par défaut (2026-10-27) : une page qui ne fournit pas
  // ses propres regroupements reçoit ceux du référentiel (concession_master des
  // sociétés), seulement quand ils réunissent au moins deux sociétés — la
  // concession d'une seule société doublerait sa ligne. Cocher une concession
  // écrit le paramètre `societes` de l'adresse, lu par toutes les pages.
  const { data: ref_ } = useBi();
  const [params, setParams] = useSearchParams();
  const groupesDefaut = useMemo(() => {
    if (groupes || !societesDispo) return null;
    const codes = new Set(societesDispo.map((x) => x.code));
    const m = new Map();
    (ref_.societes || []).filter((x) => codes.has(x.code) && x.master).forEach((x) => {
      if (!m.has(x.master)) m.set(x.master, []);
      m.get(x.master).push(x.code);
    });
    const valeurs = [...m.entries()].filter(([, l]) => l.length > 1)
      .map(([nom, membres]) => ({ code: `master:${nom}`, nom, membres }));
    return valeurs.length ? [{ titre: 'Concessions groupées', valeurs }] : null;
  }, [groupes, societesDispo, ref_.societes]);
  const basculerGroupeDefaut = (v) => {
    const next = new URLSearchParams(params);
    const val = basculerGroupe(v, societesActives, societesDispo);
    if (val) next.set('societes', val); else next.delete('societes');
    setParams(next, { replace: true });
  };
  const groupesAffiches = groupes || groupesDefaut;
  const toggleGroupe = groupes ? onToggleGroupe : basculerGroupeDefaut;

  if (!ouvert) {
    return (
      <aside className="fp-rail" ref={ref}>
        <button className="fp-rail-bouton" onClick={onToggle} title="Ouvrir les filtres" aria-label="Ouvrir les filtres">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          {nbActifs > 0 && <span className="fp-badge">{nbActifs}</span>}
        </button>
      </aside>
    );
  }

  const toutes = societesActives.length === societesDispo.length;

  const SectionCategories = categoriesDispo && categoriesDispo.length > 0 ? (
    <div className="fp-section">
      <div className="fp-section-titre">{titreCategories || 'Catégorie'}</div>
      {categoriesDispo.map((c) => (
        <label key={c.code} className="fp-ligne">
          <input type="checkbox" checked={categoriesActives.includes(c.code)} onChange={() => onToggleCategorie(c.code)} />
          {c.couleur && <span className="fp-dot" style={{ background: categoriesActives.includes(c.code) ? c.couleur : 'var(--border-strong, #c9d0d9)' }} />}
          <span>{c.nom}</span>
        </label>
      ))}
    </div>
  ) : null;

  return (
    <aside className="fp-panneau" aria-label="Filtres" ref={ref}>
      <div className="fp-tete">
        <span className="fp-titre">Filtres{nbActifs > 0 && <span className="fp-badge">{nbActifs}</span>}</span>
        <button className="fp-replier" onClick={onToggle} title="Replier les filtres" aria-label="Replier les filtres">»</button>
      </div>

      {periodes && periodes.length > 0 ? (
        <div className="fp-section">
          <div className="fp-section-titre">Période</div>
          {periodes.map((p) => (
            <label key={p.code} className="fp-ligne">
              <input type="radio" name="fp-periode" checked={periode === p.code} onChange={() => onPeriode(p.code)} />
              <span>{p.label}</span>
            </label>
          ))}
          {moisAutres && moisAutres.length > 0 && (
            <select className="fp-select" value={moisAutres.some((m) => m.code === periode) ? periode : ''}
              onChange={(e) => e.target.value && onPeriode(e.target.value)}>
              <option value="">Autre mois…</option>
              {moisAutres.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
            </select>
          )}
          {moisDispo && moisDispo.length > 0 && (
            <>
              <div className="fp-sous-titre">Ou mois précis (cumulables)</div>
              <div className="fp-mois-liste">
                {moisDispo.map((m) => (
                  <label key={m.code} className={`fp-ligne${m.desactive ? ' fp-off' : ''}`}>
                    <input type="checkbox" disabled={!!m.desactive} checked={(moisActifs || []).includes(m.code)} onChange={() => onToggleMois(m.code)} />
                    <span>{m.label}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      ) : onMois && (
        <div className="fp-section">
          <div className="fp-section-titre">Période</div>
          {PERIODES.map((p) => (
            <label key={p.mois} className="fp-ligne">
              <input type="radio" name="fp-periode" checked={mois === p.mois} onChange={() => onMois(p.mois)} />
              <span>{p.label}</span>
            </label>
          ))}
        </div>
      )}

      {/* `choix` : une section ou une liste de sections (2026-10-22). */}
      {[].concat(choix || []).filter((c) => c && c.valeurs.length > 0).map((c) => (
        <div className="fp-section" key={c.titre}>
          <div className="fp-section-titre">{c.titre}</div>
          {c.valeurs.map((v) => (
            <label key={v.code} className="fp-ligne">
              <input type="radio" name={`fp-choix-${c.titre}`}
                checked={(c.valeur || '') === v.code} onChange={() => c.onChoisir(v.code)} />
              <span>{v.nom}</span>
            </label>
          ))}
        </div>
      ))}

      {!categoriesApres && SectionCategories}

      {/* Regroupements : plaque, puis concessions groupées, puis sociétés —
          du plus large au plus fin. */}
      {(groupesAffiches || []).filter((g) => g.valeurs.length > 0).map((g) => (
        <div className="fp-section" key={g.titre}>
          <div className="fp-section-titre">{g.titre}</div>
          {g.valeurs.map((v) => {
            const tous = v.membres.length > 0 && v.membres.every((m) => societesActives.includes(m));
            const partiel = !tous && v.membres.some((m) => societesActives.includes(m));
            return (
              <label key={v.code} className="fp-ligne">
                <input type="checkbox" checked={tous}
                  ref={(el) => { if (el) el.indeterminate = partiel; }}
                  onChange={() => toggleGroupe(v)} />
                <span>{v.nom}</span>
                <span className="fp-nb">{v.membres.length}</span>
              </label>
            );
          })}
        </div>
      ))}

      {!sansSocietes && (
      <div className="fp-section">
        <div className="fp-section-titre" title="La sélection des sociétés suit sur toutes les pages, comme le champ « Concession groupée » de la barre de gauche">
          Sociétés
          {/* Bascule tout / rien en un clic. Le sens « tout désélectionner »
              n'est proposé que si le rapport sait gérer une sélection vide. */}
          {(!toutes || onAucuneSociete) && (
            <button className="fp-tout" onClick={toutes ? onAucuneSociete : onToutesSocietes}>
              {toutes ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          )}
        </div>
        <div className="fp-commun">même sélection sur toutes les pages</div>
        {societesDispo.map((s) => (
          <label key={s.code} className="fp-ligne">
            <input
              type="checkbox"
              checked={societesActives.includes(s.code)}
              onChange={() => onToggleSociete(s.code)}
            />
            <span className="fp-dot" style={{ background: societesActives.includes(s.code) ? couleurDe[s.code] : 'var(--border-strong, #c9d0d9)' }} />
            <span>{s.nom}</span>
          </label>
        ))}
      </div>
      )}

      {categoriesApres && SectionCategories}

      <button className="fp-reset" onClick={onReset} disabled={nbActifs === 0}>
        Réinitialiser les filtres
      </button>
    </aside>
  );
}

export default FiltresPanel;
